// Cart Product Reward Handler - Handles clicks on product reward cards and buttons
// This file is loaded separately to ensure it works with dynamically loaded content

class CartProductRewardHandler {
  constructor() {
    this.isInitialized = false;
    this._listenerAttached = false;
    this._boundClickHandler = null;
    this.init();
  }

  init() {
    if (this.isInitialized) return;

    // Wait for DOM to be ready before attaching events
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        console.log('DOMContentLoaded - attaching product reward events');
        this.attachEvents();
      });
    } else {
      console.log('DOM already loaded - attaching product reward events immediately');
      // Use requestAnimationFrame to ensure DOM is painted
      requestAnimationFrame(() => {
        this.attachEvents();
      });
    }

    this.isInitialized = true;
    console.log('CartProductRewardHandler initialized');
  }

  attachEvents() {
    // Use delegated event listeners on the cart drawer container
    const cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) {
      console.warn('CartDrawer not found, retrying in 100ms...');
      setTimeout(() => this.attachEvents(), 100);
      return;
    }

    // Check if we already have a listener attached
    if (this._listenerAttached) {
      console.log('Product reward listeners already attached globally');
      // Verify the listener is still working by checking if CartDrawer still exists
      const currentDrawer = document.getElementById('CartDrawer');
      if (!currentDrawer) {
        console.warn('CartDrawer was removed, need to reattach');
        this._listenerAttached = false;
        this.attachEvents();
      }
      return;
    }

    // Remove any existing listener first (cleanup)
    if (this._boundClickHandler) {
      cartDrawer.removeEventListener('click', this._boundClickHandler, true);
      console.log('Removed old event listener');
    }

    // Store bound handler so we can manage it properly
    this._boundClickHandler = (event) => {
      this.handleClick(event);
    };

    // Add click listener to the cart drawer with event delegation
    // Use capture phase to ensure we catch events before other handlers
    cartDrawer.addEventListener('click', this._boundClickHandler, true);

    // Mark as attached on the instance, not the DOM element
    this._listenerAttached = true;
    console.log('✅ Product reward event listeners attached to cart drawer (capture phase)');

    // Verify attachment by checking if product rewards exist
    const rewardElements = cartDrawer.querySelectorAll('.cart-product-reward');
    console.log(`Found ${rewardElements.length} product reward elements in cart drawer`);
  }

  // Method to ensure events are working after DOM updates
  ensureEventsAttached() {
    if (!this._listenerAttached) {
      console.log('Events not attached, attaching now...');
      this.attachEvents();
    } else {
      console.log('Events already attached, verifying cart drawer exists...');
      const cartDrawer = document.getElementById('CartDrawer');
      if (!cartDrawer) {
        console.warn('CartDrawer missing, reattaching events...');
        this._listenerAttached = false;
        this.attachEvents();
      } else {
        // Verify product rewards exist
        const rewardElements = cartDrawer.querySelectorAll('.cart-product-reward');
        console.log(`Verified: ${rewardElements.length} product reward elements in cart drawer`);
      }
    }
  }

  handleClick(event) {
    // Get the actual clicked element (might be different in capture phase)
    const target = event.target;
    console.log('CartProductRewardHandler - click detected on:', target, 'tagName:', target.tagName);

    // Check if click is anywhere within a product reward
    const rewardContainer = target.closest('.cart-product-reward');
    if (!rewardContainer) {
      // Not a product reward click, ignore
      return;
    }

    console.log('✅ Click is within product reward container');

    // Check if this reward has already been added
    if (rewardContainer.dataset.productAdded === 'true') {
      console.log('Product reward already added, ignoring click');
      return;
    }

    // Check if click is on add to cart button first (highest priority)
    const addButton = target.closest('.cart-product-reward__button');
    if (addButton) {
      console.log('✅ Add button found:', addButton);
      console.log('Add button clicked - proceeding with add to cart');
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.addProductRewardToCart(addButton);
      return;
    }

    // Check if click is on product card (but not on button)
    const productCard = target.closest('.cart-product-reward__product');
    if (productCard) {
      console.log('✅ Product card found:', productCard);
      console.log('Product card clicked - navigating to PDP');
      event.preventDefault();
      event.stopPropagation();
      this.navigateToProductReward(productCard);
      return;
    }
  }

  navigateToProductReward(element) {
    const productUrl = element.dataset.productUrl;
    if (productUrl) {
      console.log('Navigating to product:', productUrl);
      window.location.href = productUrl;
    } else {
      console.warn('No product URL found for navigation');
    }
  }

  addProductRewardToCart(button) {
    if (button.disabled) {
      console.log('Button is disabled, ignoring click');
      return;
    }

    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const customPrice = button.dataset.customPrice;

    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    console.log('Adding product reward to cart:', { productId, variantId, customPrice });

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';

    // Show cart drawer loading overlay
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.showSimpleLoading) {
      cartDrawer.showSimpleLoading();
    }

    const formData = {
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
    };

    // If custom price is set, add it as a line item property
    if (customPrice) {
      formData.items[0].properties = {
        _reward_price: customPrice,
        _product_reward: productId,
      };
    }

    // Listen for cart drawer refresh completion to hide loading
    const handleCartRefreshed = () => {
      console.log('Cart drawer refreshed after product reward added');

      // Hide loading overlay after cart is fully updated
      if (cartDrawer && cartDrawer.hideSimpleLoading) {
        cartDrawer.hideSimpleLoading();
      }

      // Note: Don't reset button state here as the button is in old DOM
      // The new DOM from cart refresh will have fresh buttons

      // Remove this specific event listener
      document.removeEventListener('cart:drawer:refreshed', handleCartRefreshed);
    };

    // Add event listener before making the fetch request
    document.addEventListener('cart:drawer:refreshed', handleCartRefreshed);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Product reward added successfully:', data);

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        // Show success message with animation (before hiding the element)
        button.style.background = '#28a745';
        button.textContent = 'Added!';

        // Mark the element as added so we don't interact with it anymore
        const recommendation = button.closest('.cart-product-reward');
        if (recommendation) {
          recommendation.dataset.productAdded = 'true';

          // Hide the recommendation with animation after a brief delay to show success
          setTimeout(() => {
            recommendation.style.opacity = '0';
            recommendation.style.transform = 'translateY(-10px)';
            setTimeout(() => {
              recommendation.remove(); // Completely remove from DOM instead of hiding
            }, 300);
          }, 500);
        }

        // Trigger cart drawer update to refresh content
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems && cartDrawerItems.onCartUpdate) {
          cartDrawerItems.onCartUpdate();
        } else {
          // Fallback: if cart drawer items not found, hide loading after delay
          console.warn('cart-drawer-items not found, using fallback timeout');
          setTimeout(() => {
            handleCartRefreshed();
          }, 1200);
        }
      })
      .catch((error) => {
        console.error('Error adding product reward:', error);
        button.disabled = false;
        button.style.background = '';
        button.textContent = originalText;

        // Remove event listener on error
        document.removeEventListener('cart:drawer:refreshed', handleCartRefreshed);

        // Hide loading overlay on error
        if (cartDrawer && cartDrawer.hideSimpleLoading) {
          cartDrawer.hideSimpleLoading();
        }
      });
  }
}

// Initialize global instance
console.log('🚀 Initializing CartProductRewardHandler global instance');
window.cartProductRewardHandlerInstance = new CartProductRewardHandler();

// Listen for cart drawer refresh events to ensure handler is ready
document.addEventListener('cart:drawer:refreshed', function () {
  console.log('🔄 Cart drawer refreshed - ensuring product reward handler is ready');
  if (window.cartProductRewardHandlerInstance && window.cartProductRewardHandlerInstance.ensureEventsAttached) {
    // Use multiple animation frames to ensure DOM is fully painted and interactive
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.cartProductRewardHandlerInstance.ensureEventsAttached();
        console.log('✅ Product reward handler verified after drawer refresh');
      });
    });
  }
});

console.log('✅ Cart product reward handler loaded and ready');
